import React, { useCallback, useMemo } from 'react'
import { isPast } from 'date-fns'
import RewardAmountText from './RewardAmountText'
import ClaimButton from './ClaimButton'
import RowCatName from './Row'
import { RefereRecipient } from './HoldingDetails.type'
import { useTranslation } from 'react-i18next'
import { Holding } from '@hooks/useHoldings'
import Table from '@components/Furry/Table'

const ReferralsTable = ({
  referralRecipients,
  holding,
}: {
  referralRecipients: RefereRecipient[]
  holding: Holding
}) => {
  const { t } = useTranslation()

  const getStatus = useCallback(
    (status: string) => {
      const name = t(`holdings.details.status.${status}`)
      let className

      switch (status) {
        case 'staking':
          className =
            'font-bold text-[#222529] px-2.5 py-1 text-sm bg-[#66BB62] w-fit rounded-sm'
          break
        case 'referred':
        default:
          className =
            'font-bold text-[white] px-2.5 text-sm py-1 bg-[#25262B] w-fit rounded-sm'
          break
      }

      return <p className={className}>{name}</p>
    },
    [t],
  )

  const columns = useMemo(
    () => [
      <span key="dewicat" className="ml-3">
        {t('holdings.details.table.dewicat')}
      </span>,
      t('holdings.details.table.date'),
      t('holdings.details.table.status'),
      <span key="referralCount" className="flex gap-2 items-center">
        <p>{t('holdings.details.referralCount')}</p>
      </span>,
      <span key="tokenEarned" className="flex gap-2 items-center">
        <p>{t('holdings.details.tokenEarned')}</p>
      </span>,
      <span key="claimAll" className="flex justify-end">
        {/** TODO: Implement claim all for referrals */}
        {/* <Button ripple className="text-xs">
            {t('holdings.details.claimAll')}
          </Button> */}
      </span>,
    ],
    [t],
  )

  const sortByDate = (a: RefereRecipient, b: RefereRecipient) => {
    const dateA = a.listingAcc.createdAt.toNumber() * 1000
    const dateB = b.listingAcc.createdAt.toNumber() * 1000
    return dateB - dateA
  }

  const rows = useMemo(() => {
    if (!referralRecipients) return []

    return referralRecipients
      .slice()
      .sort(sortByDate)
      .map((recipient) => [
        <span
          key={`dewicat-${recipient.listingAcc.nft.toBase58()}`}
          className="flex gap-3 items-center w-max"
        >
          <RowCatName nft={recipient.listingAcc.nft.toBase58()} />
        </span>,
        <span
          key={`dewicat-date-${new Date(
            recipient.listingAcc.createdAt.toNumber() * 1000,
          ).toDateString()}`}
          className="text-nowrap"
        >
          {new Date(
            recipient.listingAcc.createdAt.toNumber() * 1000,
          ).toDateString()}
        </span>,
        <span
          key={`dewicat-status-${getStatus(holding.status)}`}
          className="text-nowrap"
        >
          {getStatus(holding.status)}
        </span>,
        <span
          key={`count-${recipient.publicKey}`}
          className="flex flex-col text-left gap-1"
        >
          {recipient.count.toNumber()}
        </span>,
        <span
          key={`reward-${recipient.publicKey}`}
          className="flex flex-col text-right gap-1"
        >
          <RewardAmountText
            listingEnded={isPast(recipient.listingAcc.endAt.toNumber() * 1000)}
            recipient={recipient.publicKey}
            listing={recipient.listing}
            mint={recipient.listingAcc.tokenMint}
          />
        </span>,
        <span key={`claim-${recipient.publicKey}`} className="flex justify-end">
          <ClaimButton recipient={recipient} holding={holding} />
        </span>,
      ])
  }, [referralRecipients, holding, getStatus])

  return (
    <Table
      className={{ table: 'mt-4', th: 'w-[8rem]' }}
      rows={rows}
      columns={columns}
      enablePagination
      rowsPerPage={5}
    />
  )
}

export default ReferralsTable
